/* ============================================================
   Knobs.co — Algolia-powered header search autocomplete.
   Replaces Shopify's native predictive-search dropdown.

   On every header search input (desktop + mobile), debounces
   typing → multi-query to Algolia (products + collections in
   one round-trip) → renders a custom 2-column dropdown panel.

   Native Shopify predictive-search panel is hidden via CSS so
   it doesn't appear underneath. The native component still fires
   its /search/suggest.json calls but its UI is invisible.

   On submit (Enter key or magnifier click) the form goes to
   /search?q=... where knobs-algolia-search.js takes over the SRP.

   API credentials are the same search-only key used by
   knobs-algolia-search.js — designed to be exposed in client JS.
   ============================================================ */

(function () {
  'use strict';

  const APP_ID = 'YQLK7JGQ6E';
  const SEARCH_KEY = '11defc1b66b77421e9de1469f21f250d';
  const PRODUCTS_INDEX = 'shopify_products';
  const COLLECTIONS_INDEX = 'shopify_collections';
  const SUGGESTIONS_INDEX = 'shopify_products_query_suggestions';
  // Typesense = primary engine; Algolia (above) kept as fail-open fallback only.
  const TS_HOST = '8dcxftm9ab6hl0s4p-1.a2.typesense.net';
  const TS_SEARCH_KEY = 'uaK8IihBH8YEqaZPgyLVG1GsBNsOlcpJ'; // search-only scope (public, like Algolia's)
  // body_html_safe is EXCLUDED from query_by: it made group_by queries with 4+
  // tokens pathologically slow (22s -> 47ms, verified 2026-07-09) AND let
  // description text hijack category intent (backplates ranking for 'knobs and
  // pulls'). Descriptions are still stored/displayed, just not searched.
  // named_tags_names dropped 2026-07-27: zero hits on real queries, ~5% cost.
  // upc/barcode STAY — populated since the lighting expansion (verified live).
  const TS_QUERY_BY = 'product_type,title,sku,upc,barcode,vendor,finish,tags,context_words';
  // context_words (LAST = weakest weight): curated placement words — drawer/dresser/
  // kitchen/towel… — from context-words.json via the reseed. Replaces what description
  // matching gave Algolia, without the body_html_safe cost (2026-07-09 incident).
  // Return ONLY the fields the preview renders. Full product docs are ~5KB each
  // (body_html_safe, variants, meta, options…); trimming cut the products payload
  // 31.5KB -> 7.3KB, so each keystroke transfers + JSON-parses ~4x less (2026-07-09).
  // family_key is REQUIRED here: since 2026-07-27 dedupe happens client-side
  // (see TS_OVERFETCH below) — without it every hit dedupes to `undefined`.
  const TS_PRODUCT_FIELDS = 'objectID,id,handle,image,product_image,price,compare_at_price,title,product_type,vendor,sku,family_key';
  // 2026-07-27: group_by=family_key on every keystroke was the autocomplete
  // pothole ('satin b' = 18,869ms server-side; group_by forces _text_match
  // scoring over the whole matched set). Instead: fetch TS_OVERFETCH raw
  // variant hits (~12ms) and dedupe by family_key in shapeTypesenseResults.
  // 100 (not 30): families run up to 193 variants, and dense prefixes like
  // 'crystal pu' yield only 4 distinct families from a 30-hit window.
  const TS_OVERFETCH = 100;
  const TS_URL = `https://${TS_HOST}/multi_search`;
  const MIN_CHARS = 3;
  // Was 350ms — server search is ~5ms and the warm round-trip ~200ms, so a tighter
  // debounce restores the "results as I type" feel without meaningful extra load.
  const DEBOUNCE_MS = 120;
  const MAX_PRODUCTS = 6;
  const MAX_COLLECTIONS = 5;
  const MAX_SUGGESTIONS = 5;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // Shopify CDN stores full-resolution originals; the dropdown shows them at 56px.
  // Ask Shopify to resize on the fly (?width=) so we download a ~112px thumbnail
  // instead of a ~1000px image — the fix for slow-loading autocomplete thumbnails.
  function sizedImg(url, w) {
    if (!url || url.indexOf('cdn.shopify.com') === -1 || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function ensureStyles() {
    if (document.getElementById('knobs-ap-style')) return;
    const style = document.createElement('style');
    style.id = 'knobs-ap-style';
    style.textContent = `
      /* Suppress Shopify's native predictive-search panel —
         we own the autocomplete UI now. */
      predictive-search .predictive-search.absolute,
      .js-search-results > .predictive-search.absolute {
        display: none !important;
      }

      .knobs-ap-panel {
        position: absolute;
        top: calc(100% + 4px);
        left: 0; right: 0;
        z-index: 60;
        background: var(--color-bg, #fff);
        border: 1px solid var(--color-border, #e0e0e0);
        box-shadow: 0 8px 24px rgba(0,0,0,0.10);
        max-height: min(88vh, 860px);
        overflow-y: auto;
        display: none;
      }
      .knobs-ap-panel.is-open { display: block; }

      .knobs-ap-cols {
        display: grid;
        grid-template-columns: 1.6fr 1fr;
        gap: 28px;
        padding: 18px 20px 4px;
      }
      .knobs-ap-col-h {
        font-family: var(--tk-font-body, 'Montserrat', sans-serif);
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--color-text, #2A5973);
        margin: 0 0 12px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--color-border, #EAE0D0);
      }

      .knobs-ap-product {
        display: flex; gap: 12px;
        padding: 8px; margin: 0 -8px;
        text-decoration: none; color: var(--color-text, #1d1d1f);
        align-items: flex-start;
        transition: background 0.12s ease;
      }
      .knobs-ap-product:hover,
      .knobs-ap-product.is-active { background: var(--color-bg2, #f6f7fa); }
      .knobs-ap-product__img {
        width: 56px; height: 56px;
        object-fit: contain; flex-shrink: 0;
        background: transparent;
      }
      .knobs-ap-product__info { min-width: 0; flex: 1; padding-top: 2px; }
      .knobs-ap-product__title {
        font-family: var(--tk-font-heading, 'Noto Serif', serif);
        font-size: 14px; font-weight: 400;
        line-height: 1.3;
        color: var(--color-text, #1d1d1f);
        margin: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .knobs-ap-product__title em {
        font-style: normal; font-weight: 600;
        color: var(--color-accent, #8F3627);
        background: transparent;
      }
      .knobs-ap-product__price {
        font-family: var(--tk-font-body, 'Montserrat', sans-serif);
        font-size: 12px; color: var(--color-text2);
        margin: 4px 0 0;
      }
      .knobs-ap-product__price-was {
        text-decoration: line-through;
        opacity: 0.6; margin-left: 6px;
      }

      .knobs-ap-collection {
        display: flex; align-items: center; justify-content: space-between;
        gap: 10px;
        padding: 10px 8px; margin: 0 -8px;
        font-family: var(--tk-font-body, 'Montserrat', sans-serif);
        font-size: 13px;
        color: var(--color-text, #1d1d1f);
        text-decoration: none;
        transition: background 0.12s ease, color 0.12s ease;
      }
      .knobs-ap-collection:hover,
      .knobs-ap-collection.is-active {
        background: var(--color-bg2);
        color: var(--color-accent);
      }
      .knobs-ap-collection em {
        font-style: normal; font-weight: 600;
        color: var(--color-accent, #8F3627);
        background: transparent;
      }
      .knobs-ap-collection__count {
        font-family: var(--tk-font-body);
        font-size: 11px;
        color: var(--color-text2);
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
      }

      .knobs-ap-viewall {
        display: block;
        margin: 0; position: sticky; bottom: 0; z-index: 2; box-shadow: 0 -6px 12px -6px rgba(0,0,0,0.14);
        padding: 14px;
        text-align: center;
        background: var(--color-bg2, #f6f7fa);
        font-family: var(--tk-font-body);
        font-size: 11px; font-weight: 600;
        letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--color-accent, #8F3627);
        text-decoration: none;
        border-top: 1px solid var(--color-border);
        transition: background 0.15s ease, color 0.15s ease;
      }
      .knobs-ap-viewall:hover {
        background: var(--color-accent);
        color: #fff;
      }

      .knobs-ap-suggestions {
        display: flex; flex-wrap: wrap; gap: 8px;
        padding: 0 0 14px; margin: 0 0 14px;
        border-bottom: 1px solid var(--color-border);
      }
      .knobs-ap-suggestion {
        display: inline-block;
        padding: 6px 12px;
        background: var(--color-bg2, #f6f7fa);
        border: 1px solid var(--color-border);
        border-radius: 999px;
        font-family: var(--tk-font-body);
        font-size: 13px; color: var(--color-text, #1f2430);
        text-decoration: none;
        transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
      }
      .knobs-ap-suggestion:hover {
        background: var(--color-accent, #8F3627);
        border-color: var(--color-accent, #8F3627);
        color: #fff;
      }

      .knobs-ap-empty {
        font-family: var(--tk-font-heading, 'Noto Serif', serif);
        font-size: 16px; font-weight: 400;
        color: var(--color-text2);
        text-align: center;
        padding: 36px 24px;
      }

      @media (max-width: 600px) {
        .knobs-ap-cols { grid-template-columns: 1fr; gap: 16px; }
      }
    `;
    document.head.appendChild(style);
  }

  let abort = null;

  // Typesense multi_search → Algolia-shaped { hits, queryID } per collection, so
  // renderPanel (which reads title/handle/price/…/_highlightResult.title.value)
  // needs no changes. `highlight.<field>.snippet` is the Typesense equivalent of
  // Algolia's _highlightResult.<field>.value (verified live: the cluster returns
  // BOTH a per-field `highlight` object and a flat `highlights` array — we use
  // the former since it maps 1:1 onto the Algolia shape renderPanel expects).
  // Sanitize a Typesense highlight snippet: escape ALL source HTML, then
  // restore only the <em> markers we asked Typesense to add. Defense-in-depth
  // against HTML sneaking in via product/collection titles.
  function safeSnippet(snippet) {
    return escapeHtml(snippet)
      .replace(/&lt;em&gt;/g, '<em>')
      .replace(/&lt;\/em&gt;/g, '</em>');
  }

  function shapeTypesenseResults(results) {
    // multi_search returns HTTP 200 even when individual searches fail —
    // a per-search `error` means Typesense is NOT healthy for that query;
    // throw so doSearch()'s catch falls back to Algolia instead of
    // rendering a silently empty panel.
    results.forEach(function (r) {
      if (r && r.error) { throw new Error('typesense per-search error: ' + r.error); }
    });
    const r0 = results[0] || {};
    const r1 = results[1] || {};
    const r2 = results[2] || {};

    // Ungrouped since 2026-07-27 (see TS_OVERFETCH): dedupe the flat hit list
    // by family_key, keeping the first (= best-ranked) variant per family.
    // NOTE r0.found is now the VARIANT count, not the family count — the
    // "view all" total reads slightly higher than the grouped grid's.
    const seenFamilies = new Set();
    const dedupedHits = [];
    (r0.hits || []).forEach(function (h) {
      if (!h || !h.document) return;
      const fam = h.document.family_key || h.document.objectID || ('_' + dedupedHits.length);
      if (seenFamilies.has(fam)) return;
      seenFamilies.add(fam);
      dedupedHits.push(h);
    });
    const products = {
      hits: dedupedHits.slice(0, MAX_PRODUCTS).map(function (h) {
        const doc = Object.assign({}, h.document);
        doc._highlightResult = {
          title: { value: (h.highlight && h.highlight.title && h.highlight.title.snippet && safeSnippet(h.highlight.title.snippet)) || escapeHtml(doc.title || '') }
        };
        return doc;
      }),
      nbHits: r0.found || 0,
      queryID: ''
    };

    const collections = {
      hits: (r1.hits || []).map(function (h) {
        const doc = Object.assign({}, h.document);
        doc._highlightResult = {
          title: { value: (h.highlight && h.highlight.title && h.highlight.title.snippet && safeSnippet(h.highlight.title.snippet)) || escapeHtml(doc.title || doc.handle || '') }
        };
        return doc;
      })
    };

    const suggestions = {
      hits: (r2.hits || []).map(function (h) {
        const doc = Object.assign({}, h.document);
        doc._highlightResult = {
          query: { value: (h.highlight && h.highlight.query && h.highlight.query.snippet && safeSnippet(h.highlight.query.snippet)) || escapeHtml(doc.query || '') }
        };
        return doc;
      })
    };

    return { products: products, collections: collections, suggestions: suggestions };
  }

  // True when the box no longer shows the query this request was fired for — used
  // to drop a slow in-flight response instead of painting stale previews.
  function isStale(inputEl, q) {
    return !!inputEl && (inputEl.value || '').trim() !== q;
  }

  async function doSearch(q, panel, inputEl) {
    q = (q || '').trim();
    if (q.length < MIN_CHARS) {
      panel.classList.remove('is-open');
      return;
    }

    if (abort) abort.abort();
    abort = new AbortController();

    // ── Typesense = primary engine ──
    try {
      // use_cache MUST ride as a URL query param on multi_search — Typesense
      // ignores it inside the POST body (verified against the docs 2026-07-09).
      const tsRes = await fetch(TS_URL + '?use_cache=true', {
        method: 'POST',
        headers: {
          'X-TYPESENSE-API-KEY': TS_SEARCH_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          searches: [
            {
              collection: 'products', q: q, query_by: TS_QUERY_BY, per_page: TS_OVERFETCH,
              include_fields: TS_PRODUCT_FIELDS,
              highlight_start_tag: '<em>', highlight_end_tag: '</em>', highlight_full_fields: 'title'
            },
            {
              collection: 'collections', q: q, query_by: 'title', per_page: MAX_COLLECTIONS,
              include_fields: 'title,handle',
              highlight_start_tag: '<em>', highlight_end_tag: '</em>', highlight_full_fields: 'title'
            },
            {
              collection: 'suggestions', q: q, query_by: 'query', per_page: MAX_SUGGESTIONS,
              include_fields: 'query',
              highlight_start_tag: '<em>', highlight_end_tag: '</em>', highlight_full_fields: 'query'
            }
          ]
        }),
        signal: abort.signal
      });
      if (!tsRes.ok) throw new Error('typesense autocomplete failed: ' + tsRes.status);
      const tsData = await tsRes.json();
      if (isStale(inputEl, q)) return;
      const shaped = shapeTypesenseResults(tsData.results || []);
      renderPanel(panel, q, shaped.products, shaped.collections, shaped.suggestions);
      return;
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.warn('[knobs-ap] typesense failed, falling back to algolia', e);
    }

    // ── Fallback: original Algolia multi-index /queries (Worker primary, direct Algolia backup) ──
    try {
      const ALGOLIA_URL = `https://${APP_ID}-dsn.algolia.net/1/indexes/*/queries`;
      const WORKER_URL = 'https://knobs-search-worker.dmason-494.workers.dev/1/indexes/*/queries';
      const _opts = {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': APP_ID,
          'X-Algolia-API-Key': SEARCH_KEY,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: JSON.stringify({
          requests: [
            {
              indexName: PRODUCTS_INDEX,
              params:
                `query=${encodeURIComponent(q)}` +
                `&hitsPerPage=${MAX_PRODUCTS}` +
                `&clickAnalytics=true` +
                `&attributesToRetrieve=${encodeURIComponent(JSON.stringify(['objectID','title','handle','price','compare_at_price','image','product_image','vendor','sku']))}` +
                `&attributesToHighlight=${encodeURIComponent(JSON.stringify(['title']))}` +
                `&highlightPreTag=${encodeURIComponent('<em>')}` +
                `&highlightPostTag=${encodeURIComponent('</em>')}`
            },
            {
              indexName: COLLECTIONS_INDEX,
              params:
                `query=${encodeURIComponent(q)}` +
                `&hitsPerPage=${MAX_COLLECTIONS}` +
                `&attributesToRetrieve=${encodeURIComponent(JSON.stringify(['title','handle','products_count']))}` +
                `&attributesToHighlight=${encodeURIComponent(JSON.stringify(['title']))}` +
                `&highlightPreTag=${encodeURIComponent('<em>')}` +
                `&highlightPostTag=${encodeURIComponent('</em>')}`
            },
            {
              indexName: SUGGESTIONS_INDEX,
              params:
                `query=${encodeURIComponent(q)}` +
                `&hitsPerPage=${MAX_SUGGESTIONS}` +
                `&attributesToRetrieve=${encodeURIComponent(JSON.stringify(['query']))}` +
                `&attributesToHighlight=${encodeURIComponent(JSON.stringify(['query']))}` +
                `&highlightPreTag=${encodeURIComponent('<em>')}` +
                `&highlightPostTag=${encodeURIComponent('</em>')}`
            }
          ]
        }),
        signal: abort.signal
      };
      let res;
      try { res = await fetch(WORKER_URL, _opts); if (!res.ok) throw new Error('worker ' + res.status); }
      catch (_werr) { if (_werr.name === 'AbortError') throw _werr; res = await fetch(ALGOLIA_URL, _opts); }
      if (!res.ok) throw new Error('Algolia autocomplete failed: ' + res.status);
      const data = await res.json();
      if (isStale(inputEl, q)) return;
      // results[2] (suggestions) may be absent until the QS index finishes
      // its first build — renderPanel tolerates a missing/empty result.
      renderPanel(panel, q, data.results[0], data.results[1], data.results[2]);
    } catch (e2) {
      if (e2.name === 'AbortError') return;
      console.error('[knobs-ap]', e2);
    }
  }

  function renderPanel(panel, q, products, collections, suggestions) {
    const productsHits = (products && products.hits) || [];
    const collectionsHits = (collections && collections.hits) || [];
    const suggestionsHits = (suggestions && suggestions.hits) || [];
    const productsQueryID = (products && products.queryID) || '';

    if (productsHits.length === 0 && collectionsHits.length === 0) {
      panel.innerHTML = `<div class="knobs-ap-empty">No results for &ldquo;${escapeHtml(q)}&rdquo;</div>`;
      panel.classList.add('is-open');
      return;
    }

    let html = '';

    // Popular-search suggestions (from the Query Suggestions index). Skip the
    // one that exactly echoes what the shopper already typed.
    const sugg = suggestionsHits
      .map(s => (s && s.query) || '')
      .filter(s => s && s.toLowerCase() !== q.toLowerCase())
      .slice(0, MAX_SUGGESTIONS);
    if (sugg.length > 0) {
      html += '<div class="knobs-ap-suggestions">';
      sugg.forEach(s => {
        const url = '/search?q=' + encodeURIComponent(s) + '&type=product';
        html += `<a href="${escapeAttr(url)}" class="knobs-ap-suggestion">${escapeHtml(s)}</a>`;
      });
      html += '</div>';
    }

    html += '<div class="knobs-ap-cols">';

    if (productsHits.length > 0) {
      html += '<div class="knobs-ap-col-products"><h3 class="knobs-ap-col-h">Products</h3>';
      productsHits.forEach((h, i) => {
        const variantQS = /^\d+$/.test(String(h.objectID)) ? `?variant=${encodeURIComponent(h.objectID)}` : '';
        const url = '/products/' + escapeAttr(h.handle || '') + variantQS;
        const img = sizedImg(h.image || h.product_image || '', 120);
        const titleHl = (h._highlightResult && h._highlightResult.title && h._highlightResult.title.value) || escapeHtml(h.title || '');
        const price = (h.price != null) ? '$' + Number(h.price).toFixed(2) : '';
        const compare = (h.compare_at_price && h.compare_at_price > h.price)
          ? `<span class="knobs-ap-product__price-was">$${Number(h.compare_at_price).toFixed(2)}</span>`
          : '';
        const insightsAttrs = productsQueryID
          ? ` data-object-id="${escapeAttr(String(h.objectID))}" data-query-id="${escapeAttr(productsQueryID)}" data-position="${i + 1}"`
          : '';
        html += `
          <a href="${url}" class="knobs-ap-product"${insightsAttrs}>
            ${img
              ? `<img class="knobs-ap-product__img" src="${escapeAttr(img)}" alt="${escapeAttr(h.title || '')}" loading="lazy">`
              : `<div class="knobs-ap-product__img"></div>`}
            <div class="knobs-ap-product__info">
              <div class="knobs-ap-product__title">${titleHl}</div>
              ${price ? `<div class="knobs-ap-product__price">${price}${compare}</div>` : ''}
            </div>
          </a>`;
      });
      html += '</div>';
    }

    if (collectionsHits.length > 0) {
      html += '<div class="knobs-ap-col-collections"><h3 class="knobs-ap-col-h">Collections</h3>';
      collectionsHits.forEach(c => {
        const url = '/collections/' + escapeAttr(c.handle || '');
        const titleHl = (c._highlightResult && c._highlightResult.title && c._highlightResult.title.value) || escapeHtml(c.title || c.handle || '');
        const count = (c.products_count != null) ? Number(c.products_count).toLocaleString() : '';
        html += `
          <a href="${url}" class="knobs-ap-collection">
            <span>${titleHl}</span>
            ${count ? `<span class="knobs-ap-collection__count">${count}</span>` : ''}
          </a>`;
      });
      html += '</div>';
    }

    html += '</div>';

    const totalProducts = (products && products.nbHits) || 0;
    if (totalProducts > 0) {
      const searchUrl = '/search?q=' + encodeURIComponent(q) + '&type=product';
      html += `<a href="${escapeAttr(searchUrl)}" class="knobs-ap-viewall">View all ${totalProducts.toLocaleString()} result${totalProducts !== 1 ? 's' : ''} for &ldquo;${escapeHtml(q)}&rdquo; &rarr;</a>`;
    }

    panel.innerHTML = html;
    panel.classList.add('is-open');
  }

  function setupAutocomplete(input) {
    // Find a positioned wrapper to attach the panel to
    const wrapper = input.closest('.header__search') || input.closest('form.search') || input.parentElement;
    if (!wrapper) return;

    // The .header__search has position:relative + transform; perfect anchor.
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }

    const panel = document.createElement('div');
    panel.className = 'knobs-ap-panel';
    panel.setAttribute('role', 'listbox');
    wrapper.appendChild(panel);

    // Algolia Insights: autocomplete product click → click event + remember
    // queryID so a later add-to-cart attributes the conversion to search.
    panel.addEventListener('click', (e) => {
      const a = e.target.closest('.knobs-ap-product[data-object-id]');
      if (a && window.KnobsInsights) {
        window.KnobsInsights.recordClick({
          objectID: a.getAttribute('data-object-id'),
          queryID: a.getAttribute('data-query-id'),
          position: a.getAttribute('data-position'),
          eventName: 'PDP Click from Autocomplete'
        });
      }
      // GA4: select_item from autocomplete (queryID-free attribution path).
      try {
        if (a && window.KnobsGA4) {
          window.KnobsGA4.selectItem({
            surface: 'autocomplete',
            objectID: a.getAttribute('data-object-id'),
            position: a.getAttribute('data-position'),
            query: (input && input.value || '').trim() || undefined,
            title: (a.getAttribute('data-title') ||
              (a.querySelector('.knobs-ap-product__title, [class*="title"]') || {}).textContent || '').trim() || undefined
          });
        }
      } catch (ge) {}
    });

    let timer;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      // Cancel any in-flight request immediately — the value it was fetched for
      // is now stale, so don't let it keep running through the debounce window.
      if (abort) abort.abort();
      timer = setTimeout(() => doSearch(input.value, panel, input), DEBOUNCE_MS);
    });

    // Open the TCP+TLS connection to Typesense the moment the box is focused, so
    // the first keystroke reuses a warm socket instead of paying ~0.9s of cold
    // connect+handshake to the Ohio region. (preconnect in <head> handles page
    // load; this re-warms if that idle connection has since dropped.)
    let warmed = false;
    function warmTypesense() {
      if (warmed) return; warmed = true;
      try { fetch(`https://${TS_HOST}/health`, { mode: 'cors', keepalive: true }).catch(function () {}); } catch (e) {}
    }
    input.addEventListener('focus', () => {
      warmTypesense();
      const v = (input.value || '').trim();
      if (v.length >= MIN_CHARS && panel.innerHTML) {
        panel.classList.add('is-open');
      }
    });
    input.addEventListener('pointerenter', warmTypesense);

    document.addEventListener('click', (e) => {
      if (panel.contains(e.target) || e.target === input) return;
      panel.classList.remove('is-open');
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        panel.classList.remove('is-open');
        input.blur();
      }
    });

    input.closest('form')?.addEventListener('submit', () => {
      panel.classList.remove('is-open');
    });
  }

  function init() {
    ensureStyles();
    const inputs = document.querySelectorAll('input[type="search"][name="q"]');
    Array.from(inputs)
      .filter(inp => inp.closest('store-header, .header, header.header, .header__search'))
      .forEach(setupAutocomplete);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
